import type { FormEvent } from 'react'

import { useState } from 'react'

import { Seo } from '@/components/generals/seo'
import { Reveal } from '@/components/animation/reveal'
import { AccordionItem } from '@/components/ui/accordion'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const FAQS = [
    {
        question: 'Do you develop the websites, if yes what platform do you use?',
        answer: 'Yes. I design and build production sites on modern component-based stacks, with a focus on performance, motion, and content that is easy to update.'
    },
    {
        question: 'How about mobile apps, do you include app development?',
        answer: 'I handle product and interface design for mobile, and partner with trusted engineers when native or cross-platform development is required.'
    },
    {
        question: 'Timing, how soon can we get started?',
        answer: 'Most projects kick off within one to two weeks. Share your timeline and scope and I will let you know current availability.'
    }
]

export default function ContactPage() {
    const [email, setEmail] = useState('')
    const [projectName, setProjectName] = useState('')
    const [message, setMessage] = useState('')
    const [expanded, setExpanded] = useState(false)
    const [status, setStatus] = useState<Status>('idle')

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault()
        setStatus('submitting')
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, projectName, message })
            })
            if (!res.ok) throw new Error('failed')
            setStatus('success')
            setEmail('')
            setProjectName('')
            setMessage('')
            setExpanded(false)
        } catch {
            setStatus('error')
        }
    }

    const collapsed = { maxHeight: 0, opacity: 0 }
    const open = { maxHeight: 120, opacity: 1 }

    return (
        <>
            <Seo
                title='Start a Project'
                description='Have a project in mind? Get in touch and let us build something memorable.'
            />

            <div className='section'>
                <div className='container main-hero-container'>
                    <Reveal mode='load' delay={0.15} innerClassName='perspective-load1'>
                        <h1 className='hero-text'>let&apos;s</h1>
                    </Reveal>
                    <div className='page-hero-content-with-right-detail'>
                        <Reveal mode='load' delay={0.3} innerClassName='perspective-load2'>
                            <h1 className='hero-text'>chat</h1>
                        </Reveal>
                        <div className='content'>
                            <Reveal mode='load' delay={0.45} innerClassName='perspective-load4'>
                                <div>
                                    Have a project that you think I can help with? Shoot me an
                                    email &amp; get started right away!
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </div>

            <div className='section'>
                <div className='container'>
                    <section id='contact-form' className='contact-form'>
                        <div className='w-form'>
                            {status === 'success' ? (
                                <div className='success-message'>
                                    <div>
                                        Thank you!
                                        <br />
                                        Your submission has been received.
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit}>
                                    <div className='content'>
                                        <input
                                            className='form-input large w-input'
                                            maxLength={256}
                                            name='Email'
                                            placeholder='Your Email'
                                            type='email'
                                            required
                                            value={email}
                                            onFocus={() => setExpanded(true)}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>
                                    <div
                                        className='content overflow-hidden'
                                        style={{
                                            transition:
                                                'max-height 0.45s ease, opacity 0.45s ease',
                                            ...(expanded ? open : collapsed)
                                        }}
                                    >
                                        <input
                                            className='form-input large w-input'
                                            maxLength={256}
                                            name='ProjectName'
                                            placeholder='Project *code* name'
                                            type='text'
                                            value={projectName}
                                            onChange={(e) => setProjectName(e.target.value)}
                                        />
                                    </div>
                                    <div
                                        className='content overflow-hidden'
                                        style={{
                                            transition:
                                                'max-height 0.45s ease, opacity 0.45s ease',
                                            ...(expanded ? open : collapsed)
                                        }}
                                    >
                                        <textarea
                                            maxLength={5000}
                                            name='Message'
                                            placeholder='How can I help you?'
                                            className='form-input large w-input'
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                        />
                                    </div>
                                    <div className='right-text'>
                                        <button
                                            type='submit'
                                            disabled={status === 'submitting'}
                                            className='submit-button large'
                                            style={{ cursor: 'none' }}
                                        >
                                            {status === 'submitting' ? 'Please wait...' : 'Send'}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {status === 'error' ? (
                                <div className='error-message'>
                                    <div>
                                        Oops! Something went wrong while submitting the form.
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </section>
                </div>
            </div>

            <div className='section'>
                <div className='container'>
                    <div className='perspective'>
                        <Reveal mode='view' wrapper={false} innerClassName='perspective-load1-on-view'>
                            <h2>FREQUENTLY ASKED QUESTIONS</h2>
                        </Reveal>
                    </div>
                    <div className='accordions'>
                        {FAQS.map((faq) => (
                            <AccordionItem
                                key={faq.question}
                                question={faq.question}
                                answer={faq.answer}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    )
}
